'use client';

import { useUserGetByIamId } from '@cldsystems/ws-api-client';
import { getTestId, LocalePickerTestIds } from '@shared/index';
import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import { Suspense, useEffect, useMemo, useState } from 'react';
import { Button, Image, Modal, ModalBody, ModalContent, ModalHeader } from '@/components/ui';
import useCoreConfig from '@/hooks/use-core-config';
import useLogoutOnUserConflict from '@/hooks/use-logout-on-user-conflict';
import { usePersistUserLocale } from '@/hooks/use-persist-user-locale';
import { useAppContext, useTranslation } from '@/providers/app-provider/app-provider';
import { useKeycloak } from '@/providers/keycloak-provider';
import { useDefaultLocale } from '@/providers/root-provider';
import { getLocaleCookie, setLocaleCookie } from '@/utils/locale-cookie';
import { resolveLocalizedPathname } from '@/utils/locale-href';
import { buildPathnameForPreferredLocale, parsePathLocale } from '@/utils/locale-url';

const SKIPPED_PATH_PREFIXES = ['/studio'];

const stripTrailingSlash = (p: string) => (p.length > 1 && p.endsWith('/') ? p.slice(0, -1) : p);

const maybeRedirect = (
  router: ReturnType<typeof useRouter>,
  preferredLocale: string,
  pathname: string,
  target: string,
  suffix: string,
  supported: Set<string>,
  isHomePage: boolean,
) => {
  const { localePrefix } = parsePathLocale(pathname, supported);
  const pref = preferredLocale.trim().toLowerCase();

  // Deep link already uses the preferred locale — only rewrite when switching locale.
  if (!isHomePage && localePrefix === pref) return;

  if (stripTrailingSlash(target) !== stripTrailingSlash(pathname)) {
    router.replace(`${target}${suffix}`);
  }
};

const LocaleFirstBootstrapInner = () => {
  const pathname = usePathname();
  const router = useRouter();
  const searchParams = useSearchParams();
  const coreConfig = useCoreConfig();
  const defaultLocale = useDefaultLocale() ?? coreConfig.defaultLanguageCode;
  const { t } = useTranslation();
  const { isStudioPreview } = useAppContext<{ isStudioPreview?: boolean }>();
  const [modalOpen, setModalOpen] = useState(false);

  const { isAuthenticated, isInitialized, keycloak } = useKeycloak();
  const iamId = keycloak?.idTokenParsed?.sub ?? '';
  const {
    data: userProfile,
    isLoading: isUserLoading,
    error: userProfileError,
  } = useUserGetByIamId({
    params: { iamId },
    enabled: Boolean(isAuthenticated && iamId),
  });
  useLogoutOnUserConflict(userProfileError);

  const supportedCodes = useMemo(
    () => coreConfig.supportedLanguages.map(l => l.code),
    [coreConfig.supportedLanguages],
  );
  const languagesKey = supportedCodes.join(',');

  const persistUserLocale = usePersistUserLocale();

  const localePickerHuPhrase = t?.account.localePickerTitleHungarian ?? 'Válassz nyelvet';
  const localePickerRoPhrase = t?.account.localePickerTitleRomanian ?? 'Alegeți limba';
  const localePickerModalTitle =
    defaultLocale?.toLowerCase() === 'hu'
      ? `${localePickerHuPhrase} / ${localePickerRoPhrase}`
      : `${localePickerRoPhrase} / ${localePickerHuPhrase}`;

  useEffect(() => {
    if (isStudioPreview) return;
    if (SKIPPED_PATH_PREFIXES.some(prefix => pathname.startsWith(prefix))) return;
    if (!isInitialized) {
      setModalOpen(false);
      return;
    }

    const codes = languagesKey.split(',').filter(Boolean);
    const supported = new Set(codes.map(c => c.toLowerCase()));
    const qs = searchParams.toString();
    const suffix = qs ? `?${qs}` : '';
    const { restPath } = parsePathLocale(pathname, supported);
    const isHomePage = restPath === '';

    // Single-language storefront: keep cookie + URL aligned, then exit.
    if (codes.length <= 1) {
      const only = codes[0]?.toLowerCase();
      if (!only) return;
      if (!getLocaleCookie()?.trim()) setLocaleCookie(only);
      if (!isHomePage) return;
      const target = buildPathnameForPreferredLocale(pathname, only, defaultLocale, codes);
      maybeRedirect(router, only, pathname, target, suffix, supported, true);
      return;
    }

    const iamReady = Boolean(isAuthenticated && iamId);
    if (iamReady && isUserLoading) return;

    // For deep links we use hreflang (rendered by `metadata.alternates.languages`) so we
    // translate the page slug — e.g. `/hu/products/<hu-slug>/` → `/ro/products/<ro-slug>/`,
    // not `/ro/products/<hu-slug>/` (which would 404). Falls back to `/${locale}` when the
    // page does not advertise an alternate for the target locale.
    const resolveTarget = (preferred: string): string =>
      isHomePage
        ? buildPathnameForPreferredLocale(pathname, preferred, defaultLocale, codes)
        : resolveLocalizedPathname(preferred, defaultLocale);

    if (iamReady) {
      const fromUser = userProfile?.user?.locale?.trim().toLowerCase();
      setModalOpen(false);
      if (!fromUser || !supported.has(fromUser)) return;

      // Mirror profile locale into the cookie so guest-style code paths agree.
      const cookieLocale = getLocaleCookie()?.trim().toLowerCase();
      if (cookieLocale !== fromUser) setLocaleCookie(fromUser);

      const target = resolveTarget(fromUser);
      maybeRedirect(router, fromUser, pathname, target, suffix, supported, isHomePage);
      return;
    }

    // Guest flow.
    const cookieLocale = getLocaleCookie()?.trim().toLowerCase();
    if (!cookieLocale || !supported.has(cookieLocale)) {
      setModalOpen(true);
      return;
    }
    setModalOpen(false);
    const target = resolveTarget(cookieLocale);
    maybeRedirect(router, cookieLocale, pathname, target, suffix, supported, isHomePage);
  }, [
    pathname,
    router,
    searchParams,
    languagesKey,
    defaultLocale,
    isStudioPreview,
    isInitialized,
    isAuthenticated,
    iamId,
    isUserLoading,
    userProfile?.user?.locale,
  ]);

  const handlePick = (code: string) => {
    setModalOpen(false);
    if (isAuthenticated) {
      persistUserLocale(code);
      return;
    }
    setLocaleCookie(code);
    const qs = searchParams.toString();
    const suffix = qs ? `?${qs}` : '';
    const target = resolveLocalizedPathname(code, defaultLocale);
    router.replace(`${target}${suffix}`);
  };

  if (isStudioPreview) return null;
  if (SKIPPED_PATH_PREFIXES.some(prefix => pathname.startsWith(prefix))) return null;
  if (coreConfig.supportedLanguages.length <= 1) return null;

  return (
    <Modal
      isOpen={modalOpen && isInitialized}
      data-testid={getTestId(LocalePickerTestIds.MODAL)}
      backdrop="blur"
      isDismissable={false}
      hideCloseButton
      size="md"
      aria-labelledby="locale-preference-title"
    >
      <ModalContent>
        <ModalHeader className="flex flex-col gap-1">
          <h2 id="locale-preference-title" className="text-lg font-semibold">
            {localePickerModalTitle}
          </h2>
        </ModalHeader>
        <ModalBody className="flex flex-col gap-2 pb-6">
          {coreConfig.supportedLanguages.map(lang => (
            <Button
              key={lang.code}
              variant="bordered"
              className="h-auto justify-start gap-3 py-3"
              data-testid={getTestId(LocalePickerTestIds.OPTION, { code: lang.code })}
              onPress={() => handlePick(lang.code)}
            >
              <Image
                alt=""
                src={`https://flagcdn.com/${lang.code.toLowerCase() === 'en' ? 'gb' : lang.code.toLowerCase()}.svg`}
                width={24}
                height={18}
                style={{ borderRadius: 0 }}
              />
              <span>{lang.label}</span>
            </Button>
          ))}
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

/**
 * Locale bootstrap:
 * - Authenticated users follow `useUserGetByIamId().user.locale`; the URL is rewritten to that
 *   locale on every route (including deep links) and the cookie is mirrored from it.
 * - Guests follow the `locale` cookie; the URL is rewritten to the cookie locale on every route.
 *   The picker only shows when guests have no (valid) cookie yet.
 * - Deep-link redirects use the page's `<link rel="alternate" hreflang>` tags so the slug is
 *   translated (e.g. `/hu/products/<hu-slug>/` → `/ro/products/<ro-slug>/`). When the current
 *   page advertises no alternate for the target locale we fall back to `/${locale}` instead of
 *   blindly swapping the prefix into a URL that would 404.
 */
export const LocaleFirstBootstrap = () => (
  <Suspense fallback={null}>
    <LocaleFirstBootstrapInner />
  </Suspense>
);

export default LocaleFirstBootstrap;
